import { Stack, Typography } from '@mui/material';
import type {
  InventoryResource,
  BackupVault,
  Snapshot,
} from '@repo/api-gw-sdk';
import { useState } from 'react';

import { VerticalSlider } from '@/components/slider/verticalSlider';
import { PitrSnapshotsMultipleDots } from '@/components/snapshots/pitrSnapshotsMultipleDots';
import { NativeVaultColor } from '@/theme/colorSchemes';
import { isDateValid } from '@/utils/dateTime';

import type { MutateSnapshots } from './eonSnapshotsTab';
import type { RestoreWizardFactory } from './restoreSnapshotDrawer';
import { SnapshotsRows } from './snapshotsRows';

const NUM_OF_HOURS_IN_SLIDER_STEP = 4;

export const PitrSnapshots = ({
  pitrSnapshots,
  vaults,
  selectedSnapshot,
  setSelectedSnapshot,
  entity,
  restoreWizardFactory,
  mutateSnapshots,
}: {
  pitrSnapshots: Snapshot[];
  vaults: BackupVault[];
  selectedSnapshot: string;
  setSelectedSnapshot: (snapshotId: string) => void;
  entity: InventoryResource;
  restoreWizardFactory: RestoreWizardFactory;
  mutateSnapshots: MutateSnapshots;
}) => {
  const [currentSliderHour, setCurrentSliderHour] = useState<number>(() => {
    let latestHour = 0;
    pitrSnapshots.forEach((snap) => {
      const date = isDateValid(snap.pointInTime)
        ? snap.pointInTime
        : snap.createdTime;
      if (date && date.getUTCHours() > latestHour) {
        latestHour = Math.floor(date.getUTCHours() / 4) * 4;
      }
    });
    return latestHour;
  });
  const snapshots = pitrSnapshots.filter((snap) => {
    const date = isDateValid(snap.pointInTime)
      ? snap.pointInTime
      : snap.createdTime;
    return (
      date &&
      date.getUTCHours() >= currentSliderHour &&
      date.getUTCHours() < currentSliderHour + NUM_OF_HOURS_IN_SLIDER_STEP
    );
  });

  return (
    <Stack direction='row' width='100%' height={'100%'}>
      <VerticalSlider
        numOfSteps={NUM_OF_HOURS_IN_SLIDER_STEP}
        currentSliderValue={currentSliderHour}
        setCurrentSliderValue={setCurrentSliderHour}
      />
      <div className='w-full overflow-auto'>
        {snapshots.length > 0 ? (
          <SnapshotsRows
            snapshots={snapshots}
            vaults={vaults}
            selectedSnapshot={selectedSnapshot}
            setSelectedSnapshot={setSelectedSnapshot}
            entity={entity}
            restoreWizardFactory={restoreWizardFactory}
            mutateSnapshots={mutateSnapshots}
          />
        ) : (
          <PITRSnapshotsEmptyState />
        )}
      </div>
    </Stack>
  );
};

const PITRSnapshotsEmptyState = () => {
  return (
    <Stack
      height='100%'
      alignItems='center'
      justifyContent='center'
      gap='8px'
      paddingY='24px'
    >
      <PitrSnapshotsMultipleDots fill={NativeVaultColor} />
      <Typography variant='body1' width='260px' textAlign='center'>
        {`There are no point-in-time recovery snapshots in this time slot yet.`}
      </Typography>
    </Stack>
  );
};
